import { Component, ChangeDetectionStrategy, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { NgbDate, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';

export interface DateRange {
    from: string | null;
    to: string | null;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-medarbejdere-range-picker',
    templateUrl: './medarbejdere-range-picker.component.html',
    standalone: false
})
export class MedarbejdereRangePickerComponent {
    @Input() placement = 'bottom-left';
    @Output() rangeChange = new EventEmitter<DateRange>();

    @Input() set activeYear(year: number) {
        if (year) {
            this.minDate = { year: year, month: 1, day: 1 };
            this.maxDate = { year: year, month: 12, day: 31 };
            this.startDate = { year: year, month: 1 };
        }
    }

    @Input() set range(val: DateRange | string | null) {
        const parsed = typeof val === 'string' ? (this.tryParse(val) || {}) : (val || {});
        this.fromDate = this.parseDate(parsed.from);
        this.toDate = this.parseDate(parsed.to);
    }

    @ViewChild('dp') dp!: NgbInputDatepicker;

    fromDate: NgbDate | null = null;
    toDate: NgbDate | null = null;
    hoveredDate: NgbDate | null = null;

    startDate = { year: new Date().getFullYear(), month: 1 };
    minDate = { year: new Date().getFullYear(), month: 1, day: 1 };
    maxDate = { year: new Date().getFullYear(), month: 12, day: 31 };

    get displayText(): string {
        if (!this.fromDate) { return ''; }
        const from = `${this.pad(this.fromDate.day)}-${this.pad(this.fromDate.month)}`;
        const to = this.toDate ? `${this.pad(this.toDate.day)}-${this.pad(this.toDate.month)}` : '';
        return `${from} - ${to}`;
    }

    onDateSelection(date: NgbDate) {
        if (!this.fromDate && !this.toDate) {
            this.fromDate = date;
        } else if (this.fromDate && !this.toDate && (date.after(this.fromDate) || date.equals(this.fromDate))) {
            this.toDate = date;
            this.dp.close();
        } else {
            this.toDate = null;
            this.fromDate = date;
        }
        this.emitRange();
    }

    isHovered(date: NgbDate) {
        return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
    }

    isInside(date: NgbDate) {
        return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
    }

    isRange(date: NgbDate) {
        return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
    }

    private emitRange() {
        this.rangeChange.emit({
            from: this.fromDate ? this.toIso(this.fromDate) : null,
            to: this.toDate ? this.toIso(this.toDate) : null
        });
    }

    private toIso(d: NgbDate) { return `${d.year}-${this.pad(d.month)}-${this.pad(d.day)}T00:00:00`; }
    private pad(n: number) { return String(n).padStart(2, '0'); }
    private tryParse(v: string) { try { return JSON.parse(v); } catch { return null; } }

    private parseDate(dateStr?: string | null): NgbDate | null {
        if (!dateStr) { return null; }
        const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
        return (y && m && d) ? new NgbDate(y, m, d) : null;
    }
}
